import React, { useMemo } from "react";
import { CheckCircle2, XCircle } from "lucide-react";

export function DepthChart({ players, positionsOrder, coaches = {} }) {
  // Group players by position → depth → list
  const grouped = useMemo(() => {
    const g = {};
    for (const p of players) {
      const pos = p.position || "—";
      if (!g[pos]) g[pos] = { 1: [], 2: [], 3: [] };
      const d = p.depth || 1;
      const bucket = d >= 1 && d <= 3 ? d : 1;
      g[pos][bucket].push(p);
    }
    return g;
  }, [players]);

  // Order positions by the standard positions list, then any extras
  const ordered = useMemo(() => {
    const present = Object.keys(grouped);
    const rank = (pos) => {
      const i = positionsOrder.indexOf(pos);
      return i === -1 ? 999 : i;
    };
    return present.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [grouped, positionsOrder]);

  return (
    <div className="divide-y divide-surface-3" data-testid="depth-chart">
      {ordered.map((pos) => {
        const counts = grouped[pos];
        const total = counts[1].length + counts[2].length + counts[3].length;
        const coach = coaches[pos];
        return (
          <div key={pos} className="p-4" data-testid={`depth-pos-${pos}`}>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="font-display text-2xl tracking-wider text-white uppercase">{pos}</span>
              <span className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">
                {total} player{total !== 1 ? "s" : ""}
              </span>
              {coach ? (
                <span
                  className="text-[10px] font-condensed uppercase tracking-widest text-amber border border-amber/40 bg-amber/5 px-2 py-1 flex items-center gap-1.5"
                  data-testid={`depth-coach-${pos}`}
                >
                  <span className="text-slate-500">Coach:</span> {coach.name}
                </span>
              ) : (
                <span className="text-[10px] font-condensed uppercase tracking-widest text-slate-600 italic">
                  No position coach assigned
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[1, 2, 3].map((d) => (
                <DepthColumn key={d} depth={d} players={counts[d]} pos={pos} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DepthColumn({ depth, players, pos }) {
  const label = { 1: "1st Team", 2: "2nd Team", 3: "3rd Team" }[depth];
  return (
    <div className="border border-surface-3 bg-background" data-testid={`depth-col-${pos}-${depth}`}>
      <div className="px-3 py-2 border-b border-surface-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-display text-xl text-amber leading-none">{depth}</span>
          <span className="text-[10px] font-condensed uppercase tracking-widest text-slate-400">{label}</span>
        </div>
        <span className="text-[10px] font-condensed text-slate-600">{players.length}</span>
      </div>
      <div className="p-2 min-h-[70px]">
        {players.length === 0 ? (
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-600 italic py-3 px-1">
            open slot
          </div>
        ) : (
          <div className="space-y-1">
            {players.map((p) => {
              const nameTone = p.ad_cleared ? "text-emerald-400" : "text-red-400";
              return (
                <div
                  key={p.id}
                  data-testid={`depth-player-${p.id}`}
                  className="flex items-center justify-between px-2 py-1.5 border border-transparent hover:border-surface-3 hover:bg-surface"
                >
                  <div className={`text-sm font-semibold ${nameTone}`}>
                    {p.last_name}, {p.first_name}
                  </div>
                  <div className="flex items-center gap-1">
                    {p.coach_cleared ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" title="OK to play" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-slate-600" title="Not cleared to play" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
